import { Dispatch, SetStateAction } from 'react';

import {
	CalculateEmailsProps,
	ControlVisProps,
	EventAttendees,
	GetContentNameProps,
	InputObject,
	SetNumArrState,
	getFilterNameProps,
} from './types';
import { TextInput } from './components/TextInput';
import { RichTextInput } from './components/RichTextInput';
import { Input } from './components/Input';
import { ColorInput } from './components/ColorInput';
import { ImageInput } from './components/ImageInput';
import { TargetAudience } from './components/TargetAudience';
import { ToggleInput } from './components/ToggleInput/ToggleInput';
import { Content } from './components/Content';
import { post } from 'services/api';
import { DateTime } from 'luxon';

export const INPUT_ARRAY: InputObject[] = [
	{
		Component: Input,
		id: 'campgain-name',
		isToggle: false,
		label: 'marketing-campaignForm-name',
		description: 'marketing-campaignForm-nameDescription',
		name: 'name',
	},
	{
		Component: TargetAudience,
		label: 'marketing-campaignForm-audience',
	},
	{
		Component: Content,
		label: 'marketing-campaignForm-content',
	},
	{
		Component: ColorInput,
		label: 'marketing-campaignForm-colors',
		name: 'themeColor',
	},
	{
		Component: TextInput,
		id: 'campaign-headerText',
		label: 'marketing-campaignForm-headerText',
		name: 'headerText',
	},
	{
		Component: Input,
		id: 'campaign-title',
		label: 'marketing-campaignForm-title',
		name: 'title',
	},
	{
		Component: ToggleInput,
		htmlFor: 'logoEnabled',
		label: 'marketing-campaignForm-logo',
		name: 'logoEnabled',
	},
	{
		Component: ImageInput,
		label: 'marketing-campaignForm-headerImages',
		name: 'headerImages',
	},
	{
		Component: ToggleInput,
		htmlFor: 'viewEventButtonEnabled',
		label: 'marketing-campaignForm-viewEventButton',
		name: 'viewEventButtonEnabled',
	},
	{
		Component: Input,
		id: 'campaign-eventButtonText',
		label: 'marketing-campaignForm-eventButtonText',
		name: 'eventButtonText',
	},
	{
		Component: RichTextInput,
		id: 'campaign-bodyText',
		label: 'marketing-campaignForm-bodyText',
		name: 'bodyText',
	},
	// TODO: use footer enabled form field once we add social media
	// {
	// 	Component: InputToggle,
	// 	htmlFor: 'footerEnabled',
	// 	label: 'marketing-campaignForm-footer',
	// 	name: 'footerEnabled',
	// },
	{
		Component: Input,
		id: 'campaign-customerSupportContact',
		label: 'marketing-campaignForm-customerSupportContact',
		name: 'customerSupportContact',
	},
];

// In case we decide to switch back to using the names of selected events as filter name
// export const getSelectedNames = ({
// 	cohortsData,
// 	cohortFormValue,
// 	eventsData,
// 	eventFormValue,
// }: GetAudienceNameProps) => {
// 	const names = (cohortsData || []).reduce((selectedNames, { iD, name }) => {
// 		if (cohortFormValue?.includes(iD)) {
// 			return selectedNames === '' ? name : `${selectedNames}, ${name}`;
// 		} else return selectedNames;
// 	}, '');

// 	return (eventsData || []).reduce((selectedNames, { id, name }) => {
// 		if (eventFormValue?.includes(id)) {
// 			return selectedNames === '' ? name : `${selectedNames}, ${name}`;
// 		}
// 		return selectedNames;
// 	}, names);
// };

export const getSelectedName = ({ eventsData, eventFormValue }: GetContentNameProps) => {
	const selectedEvents = eventsData?.filter(({ id }) => eventFormValue?.includes(id)) || [];

	if (selectedEvents.length === 0) {
		return undefined;
	} else if (selectedEvents.length === 1) {
		const event = selectedEvents[0];
		const anyEvent = event as any;
		const startDate: string | undefined = anyEvent.startDate;
		const tz: string | undefined = (anyEvent.timeZone || anyEvent.timezone)?.split?.(' ')?.[0];
		if (startDate && tz) {
			const formatted = DateTime.fromISO(startDate, { zone: 'utc' }).setZone(tz).toFormat('ccc, LLL d, h:mm a');
			return `${event.name} — ${formatted}`;
		}
		return event.name;
	} else {
		return `${selectedEvents.length} events selected`;
	}
};

export const changeValue = (
	setState: SetNumArrState,
	newId: number,
	setAllGroups: Dispatch<SetStateAction<boolean>>
) => {
	setState((prev) => (prev.includes(newId) ? prev.filter((id: number) => id !== newId) : [...prev, newId]));
	setAllGroups(false);
};

export const getFilterName = ({ t, allCohorts, allEvents, selectedCt }: getFilterNameProps) => {
	if (allCohorts && allEvents) return t('marketing-campaignForm-allGroupsSelected');
	if (allCohorts) return t('marketing-campaignForm-allCohortsSelected');
	if (allEvents) return t('marketing-campaignForm-allEventsSelected');
	const s = selectedCt > 1 ? 's' : '';
	return selectedCt > 0
		? t('marketing-campaignForm-selectedGroups', { selectedCt, s })
		: t('marketing-campaignForm-selectGroups');
};

export const calculateTotalEmails = async ({
	allCohorts,
	allEvents,
	cohortsData,
	eventsData,
	cohortIds,
	eventIds,
	setValue,
}: CalculateEmailsProps) => {
	let eventEmails: string[] = [];

	if (allEvents) {
		// When all events is selected, request attendees for all events
		// Backend should handle this with the all_events flag
		try {
			// Send a special request to get all event attendees
			const allAttendeesResponse = await post('/events/attendees', { all_events: true });
			eventEmails = allAttendeesResponse?.map((person: EventAttendees) => person.email) || [];
		} catch (error) {
			// Fallback: use displayed events if the all_events endpoint fails
			const displayedEventIds = eventsData?.map((e) => e.id) || [];
			if (displayedEventIds.length > 0) {
				const eventAttendees: EventAttendees[] = await post('/events/attendees', { ids: displayedEventIds });
				eventEmails = eventAttendees?.map((person) => person.email) || [];
			}
		}
	} else {
		// Normal flow for specific events
		const eventAttendees: EventAttendees[] =
			eventIds.length === 0 ? [] : await post('/events/attendees', { ids: eventIds });
		eventEmails = eventAttendees?.map((person) => person.email) || [];
	}

	const selectedCohorts = allCohorts ? cohortsData : cohortsData?.filter((cohort) => cohortIds.includes(cohort.iD));
	const cohortEmails =
		selectedCohorts?.reduce((total: string[], cohort) => [...total, ...(cohort?.emails || [])], []) || [];
	setValue('totalEmails', new Set([...eventEmails, ...cohortEmails]).size);
};

export const controlVisibility = ({ search, name, i, totalPerPage }: ControlVisProps) => {
	const isInName = name.toLowerCase().includes(search.toLowerCase());
	const isSearched = search && isInName;
	const isFirstPage = i <= 3 && !search;
	const isViewAll = totalPerPage === -1 && isInName;
	return isSearched || isFirstPage || isViewAll;
};
